import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { SuggestionDropdown } from './SuggestionDropdown';
import { theme } from '../../styles/theme';

interface BlankDefinition {
  id: string;
  type: string;
  label: string;
  placeholder: string;
  suggestions: string[];
  required: boolean;
  help_text: string;
  position: number;
  original_text?: string;
  removable?: boolean;
}

interface Props {
  blank: BlankDefinition;
  value: string | undefined;
  isActive: boolean;
  onFocus: () => void;
  onBlur: () => void;
  onChange: (value: string) => void;
}

const BlankContainer = styled.span`
  position: relative;
  display: inline-block;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    margin: 4px 0;
    width: 100%;
  }
`;

const BlankInput = styled.input<{ isFilled: boolean; isActive: boolean; width: string }>`
  border: none;
  border-bottom: 2px dashed ${props => 
    props.isFilled ? theme.colors.success : 
    props.isActive ? theme.colors.primary : 
    theme.colors.border};
  background: ${props => 
    props.isFilled ? `${theme.colors.success}10` : 
    props.isActive ? `${theme.colors.primary}10` : 
    'transparent'};
  padding: 2px 8px;
  min-width: ${props => props.width};
  max-width: 300px;
  font-family: inherit;
  font-size: inherit;
  font-weight: ${props => props.isFilled ? '600' : '400'};
  color: ${props => 
    props.isFilled ? theme.colors.text.primary : 
    props.isActive ? theme.colors.primary : 
    theme.colors.text.muted};
  transition: all 0.2s ease;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight}10;
  }
  
  &::placeholder {
    color: ${theme.colors.text.muted};
    font-style: italic;
    font-weight: 400;
  }
  
  &:hover:not(:focus) {
    background: ${props => 
      props.isFilled ? `${theme.colors.success}20` : 
      `${theme.colors.primary}05`};
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    display: block;
    width: calc(100% - 16px) !important;
    max-width: none;
    min-width: auto;
    padding: 6px 8px;
    font-size: ${theme.fontSizes.base};
    border: 2px solid ${props => 
      props.isFilled ? theme.colors.success : 
      props.isActive ? theme.colors.primary : 
      theme.colors.border};
    border-radius: ${theme.radii.md};
    margin: 4px 0;
  }
`;

const BlankLabel = styled(motion.div)`
  position: absolute;
  top: -28px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.surface};
  padding: 2px 8px;
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.primary};
  white-space: nowrap;
  box-shadow: ${theme.shadows.sm};
  pointer-events: none;
  z-index: 10;
  
  &.desktop-label {
    @media (max-width: ${theme.breakpoints.sm}) {
      display: none !important;
    }
  }
  
  &.mobile-label {
    display: none;
    
    @media (max-width: ${theme.breakpoints.sm}) {
      display: block !important;
      position: static;
      transform: none;
      text-align: left;
      width: 100%;
      padding: 4px 0;
      background: transparent;
      box-shadow: none;
      font-size: ${theme.fontSizes.sm};
      margin-bottom: 4px;
      font-weight: 600;
    }
  }
`;

const RequiredIndicator = styled.span`
  color: ${theme.colors.error};
  margin-left: 2px;
`;

export const BlankFiller: React.FC<Props> = ({
  blank,
  value,
  isActive,
  onFocus,
  onBlur,
  onChange
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [inputWidth, setInputWidth] = useState('120px');
  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);

  // Calculate input width based on content
  useEffect(() => {
    if (measureRef.current) {
      const text = value || blank.placeholder || 'blank';
      measureRef.current.textContent = text;
      const width = measureRef.current.offsetWidth + 20; // Add padding
      setInputWidth(`${Math.max(120, Math.min(300, width))}px`);
    }
  }, [value, blank.placeholder]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setIsTyping(true);
    
    // Show suggestions if input is empty
    if (e.target.value.length === 0) {
      setShowSuggestions(true);
      setIsTyping(false);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionSelect = (suggestion: string) => {
    if (suggestion === '__REMOVE_BLANK__') {
      // Special value to indicate removal
      onChange('__REMOVED__');
      setShowSuggestions(false);
    } else if (suggestion === 'Custom...') {
      // Keep input focused for custom entry
      inputRef.current?.focus();
      setShowSuggestions(false);
    } else {
      onChange(suggestion);
      setShowSuggestions(false);
      setIsTyping(false);
    }
  };

  const handleFocus = () => {
    onFocus();
    setShowSuggestions(true);
    setIsTyping(false);
    
    // Select all text on focus for easy replacement
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  const handleBlur = () => {
    // Delay to allow suggestion click
    setTimeout(() => {
      onBlur();
      setShowSuggestions(false);
    }, 200);
  };

  const displayValue = value === '__REMOVED__' ? '[Removed]' : (value || '');
  const isFilled = !!value && value.trim() !== '' && value !== '__REMOVED__';
  const isRemoved = value === '__REMOVED__';

  if (isRemoved) {
    return (
      <span style={{ 
        textDecoration: 'line-through', 
        opacity: 0.5,
        color: theme.colors.text.muted 
      }}>
        [{blank.placeholder} removed]
      </span>
    );
  }

  return (
    <BlankContainer>
      {/* Hidden span for measuring text width */}
      <span 
        ref={measureRef} 
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'pre',
          font: 'inherit'
        }}
      />
      
      <BlankInput
        ref={inputRef}
        type="text"
        value={displayValue}
        placeholder={`[${blank.placeholder}]`}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleInputChange}
        isFilled={isFilled}
        isActive={isActive}
        width={inputWidth}
        spellCheck={false}
      />
      
      <AnimatePresence>
        {isActive && (
          <BlankLabel
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="desktop-label"
          >
            {blank.label}
            {blank.required && <RequiredIndicator>*</RequiredIndicator>}
          </BlankLabel>
        )}
      </AnimatePresence>
      
      {/* Always show label on mobile */}
      <BlankLabel
        as="div"
        className="mobile-label"
      >
        {blank.label}
        {blank.required && <RequiredIndicator>*</RequiredIndicator>}
      </BlankLabel>
      
      <AnimatePresence>
        {showSuggestions && !isTyping && (
          <SuggestionDropdown
            suggestions={blank.suggestions}
            onSelect={handleSuggestionSelect}
            currentValue={value}
            showRemoveOption={blank.removable || false}
          />
        )}
      </AnimatePresence>
    </BlankContainer>
  );
};